import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import type { FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { LogoMark } from '@/components/layout/Logo'
import { UserMenu } from '@/components/layout/UserMenu'
import { Alert, Button, Card, CardHeader, TextField, mutedTextClass } from '@/components/layout/ui'
import { orgsApi } from '@/lib/api'
import { cn } from '@/lib/cn'
import { fieldErrorFor, isFormLevel, messageFor } from '@/lib/errors'
import { useSession } from '@/lib/session'

/** Turns a workspace name into the address the API would derive from it. */
function suggestSlug(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
}

/**
 * `/new-workspace` — create a team workspace.
 *
 * It sits outside the workspace shell because someone with no workspaces has to
 * be able to reach it. The address is optional: leave it blank and the server
 * derives one, which is the path that cannot collide.
 */
export default function NewWorkspacePage() {
  const navigate = useNavigate()
  const { refresh, defaultOrganization } = useSession()

  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [slugTouched, setSlugTouched] = useState(false)

  const create = useMutation({
    mutationFn: () =>
      orgsApi.create({ name: name.trim(), slug: slug.trim() ? slug.trim() : undefined }),
    onSuccess: async (workspace) => {
      await refresh()
      navigate(`/${workspace.slug}`, { replace: true })
    },
  })

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    create.mutate()
  }

  const effectiveSlug = slugTouched ? slug : suggestSlug(name)

  return (
    <div className="min-h-dvh">
      <header className="flex h-14 items-center justify-between border-b border-ink-200 px-4 dark:border-ink-800">
        <Link to="/" className="flex items-center gap-2.5" aria-label="Taskly">
          <LogoMark />
          <span className="text-sm font-semibold tracking-tight">Taskly</span>
        </Link>
        <UserMenu />
      </header>

      <main className="mx-auto max-w-lg px-6 py-12">
        <h1 className="text-xl font-semibold tracking-tight">Create a workspace</h1>
        <p className={cn('mt-1.5 text-sm', mutedTextClass)}>
          A team workspace has its own members, projects and plan. Your personal workspace stays
          separate.
        </p>

        <Card className="mt-8">
          <CardHeader title="Workspace details" />
          <form onSubmit={handleSubmit} className="space-y-4 p-5" noValidate>
            <TextField
              label="Name"
              autoFocus
              required
              placeholder="Acme Engineering"
              value={name}
              onChange={(event) => {
                setName(event.target.value)
                if (!slugTouched) setSlug(suggestSlug(event.target.value))
              }}
              error={fieldErrorFor(create.error, 'name')}
            />
            <TextField
              label="Address"
              spellCheck={false}
              autoCapitalize="off"
              placeholder="acme-engineering"
              value={effectiveSlug}
              onChange={(event) => {
                setSlugTouched(true)
                setSlug(event.target.value)
              }}
              hint={`Links will look like /app/${effectiveSlug || 'your-workspace'}. Leave it as suggested if unsure.`}
              error={fieldErrorFor(create.error, 'slug')}
            />

            {create.error && isFormLevel(create.error, ['name', 'slug']) ? (
              <Alert tone="danger">{messageFor(create.error)}</Alert>
            ) : null}

            <div className="flex items-center justify-between gap-3">
              {defaultOrganization ? (
                <Link
                  to={`/${defaultOrganization.slug}`}
                  className={cn('text-sm hover:underline', mutedTextClass)}
                >
                  Cancel
                </Link>
              ) : (
                <span />
              )}
              <Button
                type="submit"
                variant="primary"
                loading={create.isPending}
                disabled={!name.trim()}
              >
                Create workspace
              </Button>
            </div>
          </form>
        </Card>
      </main>
    </div>
  )
}
